import { supabase } from '../config/supabase';
import { generateItemNumber, generateBarcodeBlob } from '../utils/barcodeGenerator';

// Helper function to convert camelCase to snake_case for database
const mapDoorDataToDatabase = (doorData) => {
  return {
    width: doorData.width,
    height: doorData.height,
    door_name: doorData.doorName,
    has_glass: doorData.hasGlass,
    hinge_type: doorData.hingeType,
    lock_type: doorData.lockType,
    striker_type: doorData.strikerType,
    frame_paint: doorData.framePaint,
    amount: doorData.amount,
    waternose: doorData.waternose,
    threshold_type: doorData.thresholdType,
    kickplate: doorData.kickplate,
    surface_finish: doorData.surfaceFinish,
    custom_finish: doorData.customFinish,
    color_code: doorData.colorCode,
    handedness: doorData.handedness,
    glass_style: doorData.glassStyle,
    glass_width: doorData.glassWidth,
    glass_height: doorData.glassHeight,
    groove_design: doorData.grooveDesign,
    hinge_count: doorData.hingeCount,
    door_status: doorData.doorStatus,
    special_feature: doorData.specialFeature,
    item_nr: doorData.itemNr,
    barcode_url: doorData.barcodeUrl
  };
};

// Helper function to convert snake_case from database to camelCase for UI
const mapDoorDataFromDatabase = (dbData) => {
  if (!dbData) return null;
  
  return {
    id: dbData.id,
    width: dbData.width,
    height: dbData.height,
    doorName: dbData.door_name,
    hasGlass: dbData.has_glass,
    hingeType: dbData.hinge_type,
    lockType: dbData.lock_type,
    strikerType: dbData.striker_type,
    framePaint: dbData.frame_paint,
    amount: dbData.amount,
    waternose: dbData.waternose,
    thresholdType: dbData.threshold_type,
    kickplate: dbData.kickplate,
    surfaceFinish: dbData.surface_finish,
    customFinish: dbData.custom_finish,
    colorCode: dbData.color_code,
    handedness: dbData.handedness,
    glassStyle: dbData.glass_style,
    glassWidth: dbData.glass_width,
    glassHeight: dbData.glass_height,
    grooveDesign: dbData.groove_design,
    hingeCount: dbData.hinge_count,
    doorStatus: dbData.door_status,
    specialFeature: dbData.special_feature,
    itemNr: dbData.item_nr,
    barcodeUrl: dbData.barcode_url,
    folderId: dbData.folder_id,
    createdAt: dbData.created_at,
    updatedAt: dbData.updated_at
  };
};

export const doorService = {
  // Create or update door metadata
  async saveDoorMetadata(folderId, doorData) {
    try {
      // Generate item number (article number)
      const itemNr = generateItemNumber(doorData);
      console.log('📦 Generated item number:', itemNr);

      // Generate barcode and upload to Supabase Storage
      let barcodeUrl = null;
      try {
        console.log('🔄 Generating barcode for item:', itemNr);
        const barcodeBlob = await generateBarcodeBlob(itemNr);
        console.log('✅ Barcode blob generated, size:', barcodeBlob.size, 'bytes');
        
        const fileName = `barcode_${itemNr.replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}.png`;
        console.log('📤 Uploading barcode to storage:', fileName);
        
        // Upload barcode to Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('files')
          .upload(`barcodes/${fileName}`, barcodeBlob, {
            contentType: 'image/png',
            upsert: true
          });

        if (uploadError) {
          console.error('❌ Error uploading barcode to storage:', uploadError);
          console.error('Upload error details:', JSON.stringify(uploadError, null, 2));
        } else {
          // Get public URL
          const { data: urlData } = supabase.storage
            .from('files')
            .getPublicUrl(`barcodes/${fileName}`);
          
          barcodeUrl = urlData.publicUrl;
          console.log('✅ Barcode uploaded successfully!');
          console.log('🔗 Barcode URL:', barcodeUrl);
        }
      } catch (barcodeError) {
        console.error('❌ Error generating/uploading barcode:', barcodeError);
        console.error('Barcode error stack:', barcodeError.stack);
        // Continue without barcode if generation fails
      }

      // Add item number and barcode URL to door data
      const enrichedDoorData = {
        ...doorData,
        itemNr,
        barcodeUrl
      };

      // Check if metadata already exists
      const { data: existing } = await supabase
        .from('door_metadata')
        .select('id')
        .eq('folder_id', folderId)
        .single();

      // Convert camelCase to snake_case for database
      const dbData = mapDoorDataToDatabase(enrichedDoorData);
      console.log('🚪 DoorService: Saving door data:', { doorData: enrichedDoorData, dbData });

      if (existing) {
        // Update existing
        const { data, error } = await supabase
          .from('door_metadata')
          .update(dbData)
          .eq('folder_id', folderId)
          .select()
          .single();

        if (error) throw error;
        return mapDoorDataFromDatabase(data);
      } else {
        // Create new
        const { data, error } = await supabase
          .from('door_metadata')
          .insert([{
            ...dbData,
            folder_id: folderId
          }])
          .select()
          .single();

        if (error) throw error;
        return mapDoorDataFromDatabase(data);
      }
    } catch (error) {
      console.error('Error in saveDoorMetadata:', error);
      throw error;
    }
  },

  // Get door metadata
  async getDoorMetadata(folderId) {
    const { data, error } = await supabase
      .from('door_metadata')
      .select('*')
      .eq('folder_id', folderId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return mapDoorDataFromDatabase(data);
  },

  // Delete door metadata
  async deleteDoorMetadata(folderId) {
    const { error } = await supabase
      .from('door_metadata')
      .delete()
      .eq('folder_id', folderId);

    if (error) throw error;
    return true;
  }
};

export default doorService;
